import {ActionIcon, Avatar, Group, Paper, Text, UnstyledButton} from '@mantine/core'
import {IconMinus, IconPlus, IconTruck, IconUser} from '@tabler/icons-react'

import {identityEquipmentColor, identityWorkerColor} from 'src/theme/semantic-colors'

import {RentalAssetAssignmentPeek} from '../assets/rental-assets/rental-asset-assignment-peek'
import {WorkerAssignmentPeek} from '../assets/workers/worker-assignment-peek'
import {
    type FulfillmentAssignment,
    type FulfillmentGroup,
    isFirstPartyFleetRentalAssetId,
} from './assignment-grouping-utils'

type AssignmentSlotProps = {
    group: FulfillmentGroup
    assignment?: FulfillmentAssignment
    empty?: boolean
    jobStartDate?: Date
    firstPartyRentalAssetIds?: ReadonlySet<string>
    onOpenPool?: () => void
    onRemove?: (resourceId: string) => void
}

function resourceName(assignment: FulfillmentAssignment): string {
    return assignment.workerName ?? assignment.rentalAssetName ?? 'Unnamed'
}

function resourceId(assignment: FulfillmentAssignment): string | null {
    return assignment.workerId ?? assignment.rentalAssetId
}

function AssignmentSlotBody({group, name}: {group: FulfillmentGroup; name: string}) {
    const icon = group.kind === 'worker' ? <IconUser size={16} /> : <IconTruck size={16} />

    return (
        <Group gap="sm" wrap="nowrap" style={{minWidth: 0, flex: 1}}>
            <Avatar
                radius="xl"
                size="sm"
                color={group.kind === 'worker' ? identityWorkerColor() : identityEquipmentColor()}
            >
                {icon}
            </Avatar>
            <Text size="sm" fw={500} truncate>
                {name}
            </Text>
        </Group>
    )
}

export function AssignmentSlot({
    group,
    assignment,
    empty = false,
    jobStartDate,
    firstPartyRentalAssetIds,
    onOpenPool,
    onRemove,
}: AssignmentSlotProps) {
    if (empty) {
        const content = (
            <Group gap="sm" wrap="nowrap">
                <Avatar radius="xl" size="sm" color="gray" variant="light">
                    <IconPlus size={14} />
                </Avatar>
                <div>
                    <Text size="sm" fw={500}>
                        Empty slot
                    </Text>
                    <Text size="xs" c="dimmed">
                        {group.kind === 'worker'
                            ? 'Assign worker'
                            : `Assign ${group.label.toLowerCase()}`}
                    </Text>
                </div>
            </Group>
        )

        if (onOpenPool) {
            return (
                <UnstyledButton
                    onClick={onOpenPool}
                    style={{
                        width: '100%',
                        border: '2px dashed var(--mantine-color-gray-4)',
                        borderRadius: 'var(--mantine-radius-md)',
                        padding: 'var(--mantine-spacing-sm)',
                    }}
                >
                    {content}
                </UnstyledButton>
            )
        }

        return (
            <Paper withBorder p="sm" radius="md" style={{borderStyle: 'dashed', opacity: 0.8}}>
                {content}
            </Paper>
        )
    }

    if (!assignment) {
        return null
    }

    const id = resourceId(assignment)
    const name = resourceName(assignment)
    const showRentalAssetPeek =
        group.kind === 'rental_asset' &&
        assignment.rentalAssetId != null &&
        jobStartDate != null &&
        firstPartyRentalAssetIds != null &&
        isFirstPartyFleetRentalAssetId(assignment.rentalAssetId, firstPartyRentalAssetIds)
    const showWorkerPeek =
        group.kind === 'worker' && assignment.workerId != null && jobStartDate != null

    const slotBody = <AssignmentSlotBody group={group} name={name} />

    return (
        <Paper withBorder p="sm" radius="md">
            <Group justify="space-between" wrap="nowrap" align="center">
                {showRentalAssetPeek ? (
                    <RentalAssetAssignmentPeek
                        assetId={assignment.rentalAssetId!}
                        assetName={name}
                        assetTypeName={group.label}
                        referenceDate={jobStartDate}
                    >
                        {slotBody}
                    </RentalAssetAssignmentPeek>
                ) : showWorkerPeek ? (
                    <WorkerAssignmentPeek
                        workerId={assignment.workerId!}
                        workerName={name}
                        workerTypeName={group.label}
                        referenceDate={jobStartDate}
                    >
                        {slotBody}
                    </WorkerAssignmentPeek>
                ) : (
                    slotBody
                )}

                {id && onRemove && (
                    <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Remove ${name}`}
                        onClick={() => onRemove(id)}
                    >
                        <IconMinus size={14} />
                    </ActionIcon>
                )}
            </Group>
        </Paper>
    )
}
